import { Form, message } from 'antd'
import { useCountDown } from 'ahooks'
import md5 from 'md5'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useLingui } from '@lingui/react/macro'

import { useEmailSignIn } from './emailSignIn'

import { useAnalyticsData } from '@/hooks/analyticsData'


import { useState, useRef } from 'react';
import { api } from '@/utils/api';
dayjs.extend(utc)
dayjs.extend(timezone)

export interface IUseSignUpStepsOptions {
  modifyAccountFields?: (
    values: Record<string, string>,
  ) => Record<string, string>
  onSignUpSuccess?: () => void
  /** 默认 30 秒可重发 */
  resendCodeInterval?: number
  onChangeSignUpByEmail?: (value: boolean) => void
}

export function useSignUpSteps(options: IUseSignUpStepsOptions = {}) {
  const {
    modifyAccountFields,
    onSignUpSuccess,
    resendCodeInterval = process.env.NEXT_PUBLIC_APP_ENV !== 'production'
      ? 6
      : 10,
    onChangeSignUpByEmail,
  } = options

  const { t } = useLingui()

  const [form] = Form.useForm()
  const [step, setStep] = useState(0)
  const resendEmailRef = useRef(false)

  const { handleSignIn, loading: logInLoading } =
    useEmailSignIn(onSignUpSuccess)
  const { trackCustomEvent } = useAnalyticsData()

  const signUpDoneRef = useRef(false)
  const [countdownTargetDate, setCountdownTargetDate] = useState<number>()
  const [_countdownSendCode] = useCountDown({
    targetDate: countdownTargetDate,
    onEnd: () => {
      setCountdownTargetDate(undefined)
    },
  })
  const countdownSendCode = Math.round(_countdownSendCode / 1000)
  const {
    mutateAsync: sendEmailVerifiedCode,
    isLoading: isLoadingSendEmailVerifiedCode,
  } = api.user.sendRegisterEmailCode.useMutation({
    onSuccess: async () => {
      message.success(t`Email sent!`, 5)
    },
  })
  const {
    mutateAsync: verifiedEmailCode,
    isLoading: isLoadingVerifiedEmailCode,
  } = api.user.verifyRegisterEmailCode.useMutation()

  const { mutateAsync: createAccount, isLoading: isLoadingCreateAccount } =
    api.user.createUserByVerifiedEmail.useMutation({
      onSuccess: async (res, variables) => {
        signUpDoneRef.current = true

        trackCustomEvent({
          name: 'sign_up',
          method: 'email',
          status: 0,
          meta: { origin_url: window.location.href },
          latestUserId: res.id,
          latestEmail: variables.email,
        })
        await handleSignIn({
          email: variables.email,
          // 提交后的表单 password 字段，已 md5 加密
          md5Password: variables.password,
        })
      },
    })

  const handleSendEmailVerifiedCode = async (resend = false) => {
    if (countdownSendCode) {
      // eslint-disable-next-line no-console
      console.warn('send code countdown is running...')
      return
    }
    if (!resend) {
      await form.validateFields()
    } else {
      resendEmailRef.current = true
    }
    await sendEmailVerifiedCode({
      email: form.getFieldValue('email'),
    })
    if (resend) {
      resendEmailRef.current = false
    } else {
      setStep(step + 1)
    }
    setCountdownTargetDate(Date.now() + resendCodeInterval * 1000)
  }

  const handleVerify = async () => {
    await form.validateFields()
    await verifiedEmailCode({
      email: form.getFieldValue('email'),
      code: form.getFieldValue('code'),
    })
    setStep(step + 1)
  }

  const handleCreate = async () => {
    await form.validateFields()
    const values = form.getFieldsValue(true)
    const mergedValues = {
      ...values,
      password: md5(values.password),
      timeZone: dayjs.tz.guess(),
    }

    if (signUpDoneRef.current) {
      handleSignIn({ email: values.email, password: values.password })
      return
    }
    await createAccount(
      modifyAccountFields ? modifyAccountFields(mergedValues) : mergedValues,
    )
  }

  const handleNext = async () => {
    const stepCallbacks = [
      handleSendEmailVerifiedCode,
      handleVerify,
      handleCreate,
    ]
    const callback = stepCallbacks[step]
    if (!callback) {
      throw new Error(`Unexpected step callback of [${step}]`)
    }
    await callback()
  }

  const backable = [1].includes(step)

  const handleBack = () => {
    if (backable) {
      if (step === 0) {
        onChangeSignUpByEmail?.(false)
        return
      }
      setStep(step - 1)
      return
    }
    // eslint-disable-next-line no-console
    console.warn('Back ignored, only step 1 can be back to 0.')
  }

  return {
    loading:
      (isLoadingSendEmailVerifiedCode && !resendEmailRef.current) ||
      isLoadingVerifiedEmailCode ||
      isLoadingCreateAccount ||
      logInLoading,
    countdownSendCode,
    backable,
    step,
    handleNext,
    handleBack,
    sendEmailVerifiedCode: (flag = true) => handleSendEmailVerifiedCode(flag),
    handleCreate,
    handleVerify,
    form,
    isLoadingSendEmailVerifiedCode,
  }
}
